import { Box, Heading } from '@chakra-ui/react';
import {
  ICalendlyEvent,
  IGoToMeetingConferenceData,
  IInviteeQuestions,
  IMicrosoftTeamsConferenceData,
  IScheduledEvent,
  IZoomConferenceData,
  LocationType,
} from 'api-paths-cas';
import { getDateRangeInFullFormat } from '../../../../utilities/formatting';

interface IProps {
  interview: ICalendlyEvent;
  event: IScheduledEvent;
}

const EventRow = ({ id, label, value }: { id: string; label: string; value: string | undefined }) => {
  return (
    <>
      {value ? (
        <Box key={`${id}-row`} data-selector={`${id}-row`} mb={4}>
          <Box key={`${id}-value`} data-selector={`${id}-value`}>
            {label && (
              <>
                <Box fontWeight={'medium'} color={'muted'}>
                  {label}
                </Box>
              </>
            )}

            {value}
          </Box>
        </Box>
      ) : null}
    </>
  );
};

const BookingDetailsSection: React.FC<IProps> = (props: IProps) => {
  const { interview, event } = props;

  const AdditionQuestions = () => {
    return (
      <>
        {(event.additionalQuestions ?? []).map((item: IInviteeQuestions, index: number) => {
          const { question, answer } = item;
          return <EventRow id={`question-${index}`} key={`question-${index}`} label={question} value={answer} />;
        })}
      </>
    );
  };

  const ConferenceDetails = () => {
    const type = event.location?.type ?? '';
    switch (type) {
      case LocationType.MicrosoftTeamsConference: {
        const metadata = event.location?.metadata as IMicrosoftTeamsConferenceData;
        return (
          <>
            <Heading as="h3" fontSize="lg" fontWeight="medium">
              Microsoft Teams Conference Details
            </Heading>
            <EventRow id={`conference-id`} label={'ID'} value={metadata?.id} />
            <EventRow
              id={`conference-audioConferenceId`}
              label={'Audio Conference ID'}
              value={metadata?.audioConferenceId}
            />
            <EventRow id={`conference-dialinUrl`} label={'Dial-in URL'} value={metadata?.dialinUrl} />
            <EventRow id={`conference-tollNumber`} label={'Toll Number'} value={metadata?.tollNumber} />
          </>
        );
      }
      case LocationType.ZoomConference: {
        const metadata = event.location?.metadata as IZoomConferenceData;
        return (
          <>
            <Heading as="h3" fontSize="lg" fontWeight="medium">
              Zoom Conference Details
            </Heading>
            <EventRow id={`conference-id`} label={'ID'} value={metadata?.id} />
            <EventRow id={`conference-password`} label={'Password'} value={metadata?.password} />
            <EventRow id={`conference-phone`} label={'Phone Number'} value={metadata?.phoneNumber} />
            <EventRow
              id={`conference-country`}
              label={'Country'}
              value={metadata?.country ? `${metadata.country} [${metadata.countryCode}]` : undefined}
            />
            <EventRow id={`conference-city`} label={'City'} value={metadata?.city} />
            <EventRow id={`conference-type`} label={'Type'} value={metadata?.type} />
            <EventRow
              id={`conference-intlNumbersUrl`}
              label={'International Dial-in Numbers URL'}
              value={metadata?.intlNumbersUrl}
            />
          </>
        );
      }
      case LocationType.GoToMeetingConference: {
        const metadata = event.location?.metadata as IGoToMeetingConferenceData;
        return (
          <>
            <Heading as="h3" fontSize="lg" fontWeight="medium">
              {'GoToMeeting Conference Details'}
            </Heading>
            <EventRow id={`conference-id`} label={'ID'} value={metadata?.id} />
            <EventRow id={`conference-callInfo`} label={'Call Info'} value={metadata?.conferenceCallInfo} />
          </>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <EventRow id={'event-name'} label="Event Name" value={interview.name} />
      <EventRow id={'event-description'} label="Event Description" value={interview.description} />
      <EventRow
        id={'event-datetime'}
        label="Date & Time"
        value={getDateRangeInFullFormat(event.startDate, event.endDate)}
      />
      <EventRow id={'event-location'} label="Location" value={event.location?.location} />
      <EventRow id={'event-join-url'} label="Join URL" value={event.location?.joinUrl} />
      {Object.keys(event.location?.metadata ?? {}).length ? <ConferenceDetails /> : null}
      {event.additionalQuestions?.length ? <AdditionQuestions /> : null}
    </>
  );
};

export default BookingDetailsSection;
